import { Tenant, cardsToShowLastFourOnRewardsBanner } from "redmond";
import { isCorpTenant } from "../utils/getTenant";

export const DO_NOT_APPLY_REWARDS_KEY = "do-not-apply-rewards";

type RewardProduct = "flights" | "rental cars" | "hotels" | "vacation rentals";

type RewardProductsEarnRate = {
  earnRate: number | string | undefined;
  rewardProducts: RewardProduct[];
};

export function REWARDS_BANNER_TEXT({
  firstName,
  earnRate,
  flightEarnRate,
  carEarnRate,
  homeEarnRate,
  productId,
  lastFour,
  currency,
  isPrimary,
  tenant,
}: {
  firstName: string;
  earnRate: string | number;
  carEarnRate?: string | number;
  flightEarnRate: string | number;
  homeEarnRate?: string | number;
  productId: string;
  lastFour: string;
  currency: string;
  isPrimary: boolean;
  tenant: Tenant;
}) {
  const isCorp = isCorpTenant(tenant);
  const multiplier = currency.includes("cash") ? "%" : "X";

  let earnString = "";

  if (isCorp) {
    if (!isPrimary) {
      return `Hi ${firstName}, travel smarter and get 24/7 traveler support with Capital One Business Travel.`;
    }

    earnString =
      flightEarnRate && carEarnRate && flightEarnRate === carEarnRate
        ? `Hi ${firstName}, earn ${flightEarnRate}X ${currency} on flights and rental cars when you or employees book with your ${productId} account`
        : flightEarnRate
          ? `Hi ${firstName}, earn ${flightEarnRate}${multiplier} ${currency} on flights and ${earnRate}${multiplier} ${currency} on hotels and rental cars when you or employees book with your ${productId} account`
          : `Hi ${firstName}, earn ${earnRate}${multiplier} ${currency} on hotels and rental cars when you or employees book with your ${productId} account`;

    earnString += cardsToShowLastFourOnRewardsBanner.includes(
      productId.toLowerCase()
    )
      ? ` ending in ${lastFour}.`
      : `.`;
  } else {
    if (productId.toLowerCase().includes("paradise")) {
      return `Capital One Travel helps cardholders book confidently, with smarter tools and thousands of trip options.`;
    }

    const groupedEarnRates: RewardProductsEarnRate[] = [];
    const individualEarnRates: RewardProductsEarnRate[] = [
      { earnRate: flightEarnRate, rewardProducts: ["flights"] },
      { earnRate: earnRate, rewardProducts: ["hotels"] },
      { earnRate: carEarnRate, rewardProducts: ["rental cars"] },
      { earnRate: homeEarnRate, rewardProducts: ["vacation rentals"] },
    ];
    individualEarnRates.forEach((item) => {
      if (item.earnRate) {
        const existingGroup = groupedEarnRates.find(
          (group) => group.earnRate === item.earnRate
        );
        if (existingGroup) {
          existingGroup.rewardProducts.push(...item.rewardProducts);
        } else {
          groupedEarnRates.push(item);
        }
      }
    });
    const groupedEarnRatesStrings: string[] = [];
    groupedEarnRates.forEach((item) => {
      const productsString = joinStrings(item.rewardProducts, true);
      groupedEarnRatesStrings.push(
        `${item.earnRate}${multiplier} ${currency} on ${productsString}`
      );
    });
    const groupedEarnRatesString = joinStrings(groupedEarnRatesStrings, false);
    if (groupedEarnRatesString.length > 0) {
      earnString = `Hi ${firstName}, earn ${groupedEarnRatesString}, with`;
      earnString += cardsToShowLastFourOnRewardsBanner.includes(
        productId.toLowerCase()
      )
        ? ` your ${productId} account ending in ${lastFour}.`
        : ` ${productId}.`;
    }
  }

  return earnString;
}

const joinStrings = (
  strArr: string[],
  useAmpersandForPairs: boolean = false
) => {
  const len = strArr.length;
  if (len === 0) return "";
  if (len === 1) return strArr[0];
  if (len === 2 && useAmpersandForPairs) return strArr.join(" & ");
  return strArr.slice(0, -1).join(", ") + " and " + strArr[len - 1];
};

// capone-corporate travel users clarity redirect param
export const VIEW_PARAM = "view";
export const USERS_CLARITY_PARAM_VALUE_ADD = "add_traveler";
export const USERS_CLARITY_PARAM_VALUE_LIST = "list_traveler";

// HOME PAGE
export const CORP_HOMEPAGE_TITLE = "Welcome to Capital One Business Travel";
export const CORP_HOMEPAGE_SUBTITLE = "The smarter way to book business travel";

export const AMADEUS = "Amadeus";

export const VXB = "Venture X Business";

export const EASE_ONBOARDING_PRIMARY_ACTIVATION_CHANNEL =
  "accountonboardingcobt_milescard_PR";

export enum ReferrerSource {
  BookingPortal = "booking_portal",
  AdminPortal = "admin_portal",
  Onboarding = "onboarding",
}

export const REFERRER_SOURCE = "referrer_source";

export const getReferrerSource = (source: ReferrerSource) =>
  `${new URLSearchParams({ [REFERRER_SOURCE]: source }).toString()}`;

export const BOOKING_PORTAL_REFERRER_SOURCE = getReferrerSource(
  ReferrerSource.BookingPortal
);

export const ONBOARDING_REFERRER_SOURCE = getReferrerSource(
  ReferrerSource.Onboarding
);

// Local and Session storage keys
export const HAS_CHOSEN_ACCOUNT = "cotb_has_chosen_account";

// Roles
export const CORPORATE_ROLE_ADMIN = "Admin";
